import { cn } from "@au-schein-de/au-ui-library";
import {
	type Cart,
	getCartItemQuantityDecrementStep,
	getCartItemQuantityMaximum,
	isCartItemQuantityWithinCapacity,
} from "@dransay/api/models";
import { useTranslation } from "react-i18next";
import { Link, useLoaderData, useRouteLoaderData } from "react-router";
import { Price } from "~/components/price.component";
import { ProductImage } from "~/components/product-image.component";
import { ProductSoldOut } from "~/components/product-sold-out.component";
import { PriceComparisonDiscountBadge } from "~/features/price-comparison/price-comparison-discount-badge.component";
import { useCurrency, useCurrencyPrice } from "~/hooks/use-currency-price.hook";
import { pushCartQuantityChangedMetric } from "~/tooling/sentry";
import { captureEvent } from "~/tooling/telemetry/capture-event";
import { cartProductClickedEvent } from "~/tooling/telemetry/events";
import { getProductDetailsUrl } from "~/utils/routes";
import type { Route as RootRoute } from "../../+types/root";
import type { Route } from "../../routes/+types/_storefront";
import { useCart } from "./cart.context";
import { CartButtons } from "./cart-buttons.component";

type CartItemProps = {
	index: number;
	isLoading: boolean;
	item: Cart["items"][number];
};

export function CartItem({ index, isLoading, item }: Readonly<CartItemProps>) {
	const { t } = useTranslation("cart");
	const currency = useCurrency();

	const {
		vendor,
		deliveryMethodUuid,
		getIncrementStep,
		remainingCapacity,
		increaseCartItemQuantity,
		decreaseCartItemQuantity,
		remainingItemsLimit,
		cartId,
		cartDisclosure,
	} = useCart();
	const { currencyPrice } = useCurrencyPrice(
		Number(item.product.price) * item.quantity,
	);

	const { stockValidationFlagEnabled } =
		useLoaderData<Route.ComponentProps["loaderData"]>();

	const rootLoaderData =
		useRouteLoaderData<RootRoute.ComponentProps["loaderData"]>("root");
	const enableSentryCustomEvents =
		rootLoaderData?.featureFlags?.enableSentryCustomEvents ?? false;
	const incrementStep = getIncrementStep(
		item.product.category,
		item.product.packageSize,
	);
	const maximumQuantity = getCartItemQuantityMaximum({
		category: item.product.category,
		incrementStep,
	});
	const decrementStep = getCartItemQuantityDecrementStep({
		quantity: item.quantity,
		incrementStep,
	});
	const isIncrementWithinCapacity = isCartItemQuantityWithinCapacity({
		category: item.product.category,
		quantity: incrementStep,
		remainingCapacity,
	});

	const isItemUnavailable =
		stockValidationFlagEnabled && !item.product.available;
	const productDetailsUrl = getProductDetailsUrl(
		item.product.id,
		item.product.name,
		item.product.kind,
	);
	const handleProductClick = () => {
		cartDisclosure.close();
		captureEvent(cartProductClickedEvent(item.product, currency, index));
	};

	return (
		<li
			className={cn(
				"pt-4 px-4 md:px-6 md:first:pt-0",
				isItemUnavailable && "bg-gray-100 pt-4 pb-2 mt-4 first:mt-0",
				remainingItemsLimit === 0 && !isItemUnavailable && "first:pt-0",
			)}
			data-testid="cart-item-product"
		>
			{isItemUnavailable && (
				<ProductSoldOut
					text={t("productSoldOut.title")}
					color="gray"
					withIcon={true}
				/>
			)}
			<div
				className={cn(
					"grid grid-cols-[70px_minmax(0,1fr)] w-full gap-x-2",
					isItemUnavailable && "pt-3 pb-4",
				)}
			>
				<Link
					to={productDetailsUrl}
					className="block cursor-pointer group"
					data-testid="item-cart-image-link"
					onClick={handleProductClick}
				>
					<div className="bg-gray-100 rounded-lg w-[70px] h-[90px] flex items-center justify-center overflow-hidden">
						<ProductImage
							src={item.product.imageUrl}
							alt={item.product.name ?? ""}
							viewport="cart"
							className={cn(
								"object-contain mix-blend-multiply max-h-full max-w-full",
								isItemUnavailable && "grayscale",
							)}
						/>
					</div>
				</Link>

				<div className="flex h-[90px] min-w-0 flex-col justify-between">
					<div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-2 items-start">
						<Link
							to={productDetailsUrl}
							className="min-w-0 overflow-hidden cursor-pointer group"
							data-testid="item-cart-name-link"
							onClick={handleProductClick}
						>
							<p
								className="text-caption text-gray-600 truncate"
								data-testid="item-cart-sorte"
								title={item.product.kind ?? undefined}
							>
								{item.product.kind}
							</p>
							<p
								className="text-body-bold text-gray-700 line-clamp-2 leading-5 transition-colors group-hover:text-green-500"
								data-testid="item-cart-name"
								title={item.product.name ?? undefined}
							>
								{item.product.name}
							</p>
						</Link>

						<span
							className="shrink-0 text-body-bold text-gray-700 whitespace-nowrap"
							data-testid="item-cart-get-price"
							suppressHydrationWarning
						>
							{currencyPrice}
						</span>
					</div>

					<div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2">
						<div className="flex min-w-0 items-center gap-1 text-body-sm text-gray-600">
							<span data-testid="item-cart-unit-price">
								<Price
									price={Number(item.product.price)}
									category={item.product.category}
									kind={item.product.kind}
									variant="raw"
								/>
							</span>

							<PriceComparisonDiscountBadge
								discountPercentage={item?.priceComparison?.discountPercentage}
							/>
						</div>

						{/*
							// TODO: There should be no crushed for now, but we'll reintroduce it with JUP-463
							 <Checkbox
								label={t("crushed")}
								checked={crushed}
								onCheckedChange={(checked) => {
									updateCartItem({ id, crushed: checked === true });
								}}
							/> */}
						<CartButtons
							quantity={item.quantity}
							canAddMore={
								isIncrementWithinCapacity &&
								(maximumQuantity === null ||
									item.quantity + incrementStep <= maximumQuantity)
							}
							isLoading={isLoading}
							onAdd={(quantity) => {
								// Should never happen if you're already in cart
								if (!vendor || !deliveryMethodUuid) return;

								pushCartQuantityChangedMetric(
									{
										productId: item.id,
										productName: item.product.name,
										previousQuantity: item.quantity,
										newQuantity: item.quantity + quantity,
										changeType: "increment",
										cartId,
									},
									enableSentryCustomEvents,
								);

								increaseCartItemQuantity({
									vendorId: vendor.id,
									deliveryMethodUuid,
									item: { id: item.id, quantity, crushed: false },
								});
							}}
							onRemove={(quantity) => {
								pushCartQuantityChangedMetric(
									{
										productId: item.id,
										productName: item.product.name,
										previousQuantity: item.quantity,
										newQuantity: item.quantity - quantity,
										changeType: "decrement",
										cartId,
									},
									enableSentryCustomEvents,
								);

								decreaseCartItemQuantity({ id: item.id, quantity });
							}}
							decrementStep={decrementStep}
							incrementStep={incrementStep}
							productCategory={item.product.category ?? ""}
							productKind={item.product.kind}
							disabled={isItemUnavailable}
						/>
					</div>
				</div>
			</div>
			{isItemUnavailable && (
				<ProductSoldOut
					text={t("productSoldOut.description")}
					color="error"
					className="text-error-600 bg-error-100 text-body-sm w-full"
					withIcon={true}
					variant="destructive"
				/>
			)}
		</li>
	);
}
